// ── System Admin console (#/admin) ──
//
// Workspace-scoped page for system admins (users.is_admin): approve beta signups and
// grant/revoke the admin flag, backed by /api/admin (src/routes/admin.js). The client check
// below only decides what to show — the API re-checks the live flag on every request, so a
// 403 from any call here means the flag is gone and the page bounces to the dashboard.
//
// Shape: module state, pure render*() string builders (exported for node:test), then
// paint/bind and the exported page function.

import { checkAuth, verifySession, getUser } from '../auth.js';
import { api } from '../api.js';
import { showToast, openModal, closeModal, esc } from '../ui.js';
import { navigate, getQueryParams, setQueryParams, getNavigationId } from '../router.js';
import { initLayout, getContentEl, updateLayoutContext } from '../layout.js';
import { isSystemAdmin } from '../lib/permissions.js';
import { formatDate, renderAvatarHtml } from '../utils.js';
import { renderPager, clampOffset } from '../components/pager.js';

const PAGE_SIZE = 50;
const TABS = ['beta', 'users'];
const TAB_LABELS = { beta: 'Beta signups', users: 'Users' };

const SELF_ADMIN_HINT = 'You cannot remove your own admin access — another admin has to do it.';

// API error codes (err.message carries body.error) worth a sentence of their own.
const ERROR_TEXT = {
  cannot_demote_self: SELF_ADMIN_HINT,
  user_not_found: 'That user no longer exists.',
  signup_not_found: 'That signup no longer exists.',
  already_approved: 'That signup was already approved.',
};

const LISTS = {
  pending: {
    path: '/admin/beta-signups?status=pending',
    key: 'signups',
    render: rows => renderPendingSignups(rows),
    error: 'Failed to load pending signups',
  },
  approved: {
    path: '/admin/beta-signups?status=approved',
    key: 'signups',
    render: rows => renderApprovedSignups(rows),
    error: 'Failed to load approved signups',
  },
  users: {
    path: '/admin/users',
    key: 'users',
    render: rows => renderUsersTable(rows, getUser()?.id),
    error: 'Failed to load users',
  },
};

function newList() {
  return { rows: [], offset: 0, limit: PAGE_SIZE, total: 0, loading: false, loaded: false, failed: false };
}

let state = null;
// Bumped on every page entry and on leave: an await that resumes under a different run, or
// after another page replaced #admin-page, belongs to a page the user already left and must
// not paint, toast or navigate.
let runId = 0;

function isStale(run) {
  return run !== runId || !document.getElementById('admin-page');
}

function resetState() {
  // modalToken: owner token of the latest modal this page opened (TPT647), passed to closeModal().
  state = { tab: 'beta', pending: newList(), approved: newList(), users: newList(), notice: null, modalToken: null };
}

// ── Pure helpers ──

export function adminTabFromQuery(params) {
  const tab = params?.get?.('tab');
  return TABS.includes(tab) ? tab : 'beta';
}

export function adminErrorMessage(err, fallback) {
  const code = err?.body?.error || err?.message;
  if (typeof code === 'string' && Object.hasOwn(ERROR_TEXT, code)) return ERROR_TEXT[code];
  // Remaining API errors are machine codes (invalid_id, …) — not worth showing verbatim.
  if (err?.status >= 400 && err?.status < 500 && fallback) return fallback;
  return err?.message || fallback || 'Something went wrong';
}

// esc() serializes a text node, which leaves double quotes alone, so a value placed inside
// an attribute needs them escaped as well (same gap member-combo.js's escapeAttr() covers).
function escAttr(value) {
  return esc(value).replace(/"/g, '&quot;');
}

function dash(value) {
  return value === null || value === undefined || value === '' ? '<span class="text-muted">—</span>' : esc(String(value));
}

const LANGUAGES = { en: 'English', uk: 'Ukrainian' };

function languageName(locale) {
  return dash(Object.hasOwn(LANGUAGES, locale) ? LANGUAGES[locale] : locale);
}

// Day only, on one line, so the wide tables fit a laptop viewport; the exact time is in the
// tooltip.
function dateCell(value) {
  const d = value ? new Date(value) : null;
  if (!d || Number.isNaN(d.getTime())) return '<td class="text-muted">—</td>';
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  return `<td class="text-muted admin-nowrap" title="${escAttr(formatDate(value))}">${day}</td>`;
}

function nameCell(name) {
  return `<td class="admin-name"><span class="member-name"${name ? ` title="${escAttr(name)}"` : ''}>${dash(name)}</span></td>`;
}

function codeCell(code) {
  if (!code) return '<span class="text-muted">—</span>';
  return `<span class="admin-code-cell"><code class="admin-code">${esc(code)}</code>`
    + `<button type="button" class="btn btn-ghost btn-sm admin-copy-btn" data-copy-code="${escAttr(code)}">Copy</button></span>`;
}

export function renderPendingSignups(signups) {
  if (!signups?.length) return '<p class="text-muted">No pending signups</p>';
  return `<div class="members-table-wrap"><table class="members-table admin-table">
    <thead><tr>
      <th>Name</th><th>Email</th><th>Company</th><th>Participants</th><th>Language</th><th>Requested</th><th></th>
    </tr></thead>
    <tbody>${signups.map(s => `<tr data-signup-id="${Number(s.id)}">
        ${nameCell(s.name)}
        <td class="member-email">${esc(s.email)}</td>
        <td class="admin-company">${dash(s.company_name)}</td>
        <td>${dash(s.participants)}</td>
        <td>${languageName(s.locale)}</td>
        ${dateCell(s.created_at)}
        <td class="member-actions"><button type="button" class="btn btn-primary btn-sm admin-approve-btn" data-id="${Number(s.id)}">Approve</button></td>
      </tr>`).join('')}</tbody>
  </table></div>`;
}

export function renderApprovedSignups(signups) {
  if (!signups?.length) return '<p class="text-muted">No approved signups yet</p>';
  return `<div class="members-table-wrap"><table class="members-table admin-table">
    <thead><tr>
      <th>Name</th><th>Email</th><th>Company</th><th>Code</th><th>Code expires</th><th>Used</th><th>Approved</th>
    </tr></thead>
    <tbody>${signups.map(s => {
      const used = `${Number(s.used_count) || 0}${s.max_uses == null ? '' : ' / ' + Number(s.max_uses)}`;
      return `<tr data-signup-id="${Number(s.id)}">
        ${nameCell(s.name)}
        <td class="member-email">${esc(s.email)}</td>
        <td class="admin-company">${dash(s.company_name)}</td>
        <td>${codeCell(s.code)}</td>
        ${dateCell(s.code_expires_at)}
        <td class="admin-nowrap">${used}</td>
        ${dateCell(s.reviewed_at)}
      </tr>`;
    }).join('')}</tbody>
  </table></div>`;
}

// Shown above the lists right after an approval. The approved list is ordered by signup
// date, so the new row can land on another page — this keeps the code one click away.
export function renderApprovalNotice(notice) {
  if (!notice) return '';
  const who = notice.name ? `${esc(notice.name)} (${esc(notice.email)})` : esc(notice.email);
  const mail = notice.emailSent
    ? 'The registration code was emailed.'
    : '<strong>The email could not be sent</strong> — share the code yourself.';
  return `<div class="admin-notice${notice.emailSent ? '' : ' admin-notice--warn'}" role="status">
    <span>Approved ${who}. ${mail}</span>
    ${codeCell(notice.code)}
  </div>`;
}

// `meId` is the caller: their own flag is locked on, because the API refuses self-demotion.
export function renderUsersTable(users, meId = null) {
  if (!users?.length) return '<p class="text-muted">No users</p>';
  return `<div class="members-table-wrap"><table class="members-table admin-table">
    <thead><tr><th>User</th><th>Email</th><th>Joined</th><th>System admin</th></tr></thead>
    <tbody>${users.map(u => {
      const isMe = meId != null && String(u.id) === String(meId);
      const label = u.name || u.email;
      return `<tr data-user-id="${Number(u.id)}"${isMe ? ' class="is-me"' : ''}>
        <td><div class="member-identity">
          ${renderAvatarHtml(u, 28)}
          <span class="member-name">${dash(u.name)}</span>
          ${isMe ? '<span class="member-you">You</span>' : ''}
        </div></td>
        <td class="member-email">${esc(u.email)}</td>
        ${dateCell(u.created_at)}
        <td><input type="checkbox" class="admin-user-toggle" data-id="${Number(u.id)}" data-name="${escAttr(label)}"
          aria-label="System admin: ${escAttr(label)}"${u.is_admin ? ' checked' : ''}${isMe ? ` disabled title="${escAttr(SELF_ADMIN_HINT)}"` : ''}></td>
      </tr>`;
    }).join('')}</tbody>
  </table></div>`;
}

function renderListCard(name, title) {
  return `<div class="settings-section">
    <div class="card">
      <div class="admin-list-header"><h3>${title}</h3><div id="admin-pager-${name}"></div></div>
      <div id="admin-list-${name}"><div class="empty"><span class="spinner"></span></div></div>
    </div>
  </div>`;
}

function renderShell(tab) {
  return `<div id="admin-page">
    <div class="page-header"><h2>Admin</h2></div>
    <div class="settings-tabs" id="admin-tabs" role="tablist">
      ${TABS.map(t => `<button class="settings-tab${t === tab ? ' active' : ''}" type="button" role="tab" aria-selected="${t === tab}" data-tab="${t}">${TAB_LABELS[t]}</button>`).join('')}
    </div>
    <div class="settings-pane" data-tab="beta"${tab === 'beta' ? '' : ' hidden'}>
      <div id="admin-notice"></div>
      ${renderListCard('pending', 'Pending')}
      ${renderListCard('approved', 'Approved')}
    </div>
    <div class="settings-pane" data-tab="users"${tab === 'users' ? '' : ' hidden'}>
      ${renderListCard('users', 'Users')}
    </div>
  </div>`;
}

// ── Paint ──

function paintList(name) {
  const list = state[name];
  const slot = document.getElementById('admin-list-' + name);
  const pager = document.getElementById('admin-pager-' + name);
  if (!slot) return;
  if (list.failed) {
    slot.innerHTML = `<p class="text-muted">${esc(LISTS[name].error)}. <button type="button" class="btn btn-ghost btn-sm" data-admin-retry="${name}">Retry</button></p>`;
  } else if (!list.loaded) {
    slot.innerHTML = '<div class="empty"><span class="spinner"></span></div>';
  } else {
    slot.innerHTML = LISTS[name].render(list.rows);
  }
  if (pager) {
    pager.innerHTML = list.loaded && !list.failed
      ? renderPager({ offset: list.offset, limit: list.limit, total: list.total, scope: 'admin-' + name, disabled: list.loading })
      : '';
  }
}

function paintNotice() {
  const slot = document.getElementById('admin-notice');
  if (slot) slot.innerHTML = renderApprovalNotice(state.notice);
}

// ── Data ──

// The live admin flag is gone (revoked in another session). Refresh the cached user so the
// sidebar link hides, and leave. Bumping runId silences every other in-flight request.
async function leaveIfForbidden(err) {
  if (err?.status !== 403) return false;
  runId++;
  await verifySession();
  showToast('Admin access required', 'error');
  navigate('/');
  return true;
}

function fetchPage(name, offset) {
  const { path } = LISTS[name];
  return api.get(`${path}${path.includes('?') ? '&' : '?'}limit=${state[name].limit}&offset=${offset}`);
}

// One page of a list. An offset left past the end (the last row of the last page was just
// approved) is clamped back onto a real page and refetched once.
async function loadList(name, { offset } = {}) {
  const run = runId;
  const list = state[name];
  if (offset !== undefined) list.offset = Math.max(0, offset);
  list.loading = true;
  list.failed = false;
  paintList(name);
  try {
    let res = await fetchPage(name, list.offset);
    if (isStale(run)) return;
    const clamped = clampOffset(list.offset, list.limit, Number(res.total) || 0);
    if (clamped !== list.offset) {
      list.offset = clamped;
      res = await fetchPage(name, clamped);
      if (isStale(run)) return;
    }
    list.rows = res[LISTS[name].key] || [];
    list.total = Number(res.total) || 0;
    list.loaded = true;
  } catch (err) {
    if (isStale(run) || await leaveIfForbidden(err)) return;
    list.failed = true;
    showToast(adminErrorMessage(err, LISTS[name].error), 'error');
  }
  list.loading = false;
  paintList(name);
}

function loadTab(tab) {
  if (tab === 'users') {
    if (!state.users.loaded && !state.users.loading) void loadList('users');
    return;
  }
  if (!state.pending.loaded && !state.pending.loading) void loadList('pending');
  if (!state.approved.loaded && !state.approved.loading) void loadList('approved');
}

// ── Actions ──

async function approveSignup(btn) {
  const run = runId;
  const id = Number(btn.dataset.id);
  const signup = state.pending.rows.find(s => Number(s.id) === id);
  btn.disabled = true;
  btn.textContent = 'Approving…';
  try {
    const res = await api.post(`/admin/beta-signups/${id}/approve`, {});
    if (isStale(run)) return;
    const emailSent = res.email_sent !== false;
    state.notice = { name: signup?.name, email: signup?.email || '', code: res.code, emailSent };
    paintNotice();
    showToast(
      emailSent ? `Approved — code ${res.code} emailed` : `Approved, but the email was not sent — code ${res.code}`,
      emailSent ? 'success' : 'error'
    );
  } catch (err) {
    if (isStale(run) || await leaveIfForbidden(err)) return;
    // 404/409: someone else already handled this row — fall through and refresh the lists.
    const stale = err.status === 404 || err.status === 409;
    showToast(adminErrorMessage(err, 'Failed to approve signup'), stale ? 'info' : 'error');
    if (!stale) {
      btn.disabled = false;
      btn.textContent = 'Approve';
      return;
    }
  }
  await Promise.all([loadList('pending'), loadList('approved')]);
}

async function copyCode(code) {
  try {
    await navigator.clipboard.writeText(code);
    showToast('Code copied', 'success');
  } catch {
    showToast('Copy failed — select and copy manually', 'error');
  }
}

// The checkbox has already flipped when this runs; anything but a saved change flips it back.
function confirmAdminChange(input) {
  const run = runId;
  const id = Number(input.dataset.id);
  const grant = input.checked;
  const name = input.dataset.name || 'this user';
  let saved = false;
  state.modalToken = openModal(grant ? 'Grant system admin' : 'Remove system admin', `
    <p>${grant
      ? `Make <strong>${esc(name)}</strong> a system admin? They get full access to every project and to this admin console.`
      : `Remove system admin access from <strong>${esc(name)}</strong>?`}</p>
    <div class="modal-footer">
      <button class="btn btn-ghost" id="modal-cancel">Cancel</button>
      <button class="btn ${grant ? 'btn-primary' : 'btn-danger'}" id="admin-confirm-btn">${grant ? 'Grant access' : 'Remove access'}</button>
    </div>`, {
    onClose() {
      if (!saved) input.checked = !grant;
    },
    onMount(overlay) {
      overlay.querySelector('#modal-cancel').addEventListener('click', closeModal);
      const confirmBtn = overlay.querySelector('#admin-confirm-btn');
      confirmBtn.addEventListener('click', async () => {
        confirmBtn.disabled = true;
        try {
          const res = await api.patch(`/admin/users/${id}/admin`, { is_admin: grant });
          if (isStale(run)) return;
          saved = true;
          closeModal();
          state.users.rows = state.users.rows.map(u => (Number(u.id) === id ? res.user : u));
          paintList('users');
          showToast(grant ? 'System admin access granted' : 'System admin access removed', 'success');
        } catch (err) {
          if (isStale(run)) return;
          closeModal();
          if (await leaveIfForbidden(err)) return;
          showToast(adminErrorMessage(err, 'Failed to update admin access'), 'error');
          if (err.status === 404) void loadList('users');
        }
      });
    },
  });
}

function activateTab(tab) {
  const root = document.getElementById('admin-page');
  if (!root || !TABS.includes(tab)) return;
  state.tab = tab;
  root.querySelectorAll('.settings-tab').forEach(btn => {
    const active = btn.dataset.tab === tab;
    btn.classList.toggle('active', active);
    btn.setAttribute('aria-selected', String(active));
  });
  root.querySelectorAll('.settings-pane').forEach(pane => { pane.hidden = pane.dataset.tab !== tab; });
  const params = getQueryParams();
  if (tab === 'beta') params.delete('tab'); else params.set('tab', tab);
  setQueryParams(params);
  loadTab(tab);
}

// Listeners sit on #admin-page, which the next page's innerHTML write discards — nothing
// to unbind on leave.
function bind() {
  const root = document.getElementById('admin-page');
  if (!root) return;

  root.addEventListener('click', (e) => {
    const tabBtn = e.target.closest('.settings-tab');
    if (tabBtn) { activateTab(tabBtn.dataset.tab); return; }

    const pagerBtn = e.target.closest('button[data-pager][data-pager-scope^="admin-"]');
    if (pagerBtn) {
      const name = pagerBtn.dataset.pagerScope.slice('admin-'.length);
      const list = Object.hasOwn(LISTS, name) ? state[name] : null;
      if (!list || pagerBtn.disabled || list.loading) return;
      const delta = pagerBtn.dataset.pager === 'prev' ? -1 : 1;
      void loadList(name, { offset: list.offset + delta * list.limit });
      return;
    }

    const retryBtn = e.target.closest('[data-admin-retry]');
    if (retryBtn && Object.hasOwn(LISTS, retryBtn.dataset.adminRetry)) {
      void loadList(retryBtn.dataset.adminRetry);
      return;
    }

    const approveBtn = e.target.closest('.admin-approve-btn');
    if (approveBtn) { if (!approveBtn.disabled) void approveSignup(approveBtn); return; }

    const copyBtn = e.target.closest('[data-copy-code]');
    if (copyBtn) void copyCode(copyBtn.dataset.copyCode);
  });

  root.addEventListener('change', (e) => {
    const toggle = e.target.closest('.admin-user-toggle');
    if (toggle) confirmAdminChange(toggle);
  });
}

// ── Page ──

export async function adminPage() {
  // Read before the first await: once it changes, the user navigated on and this mount stops
  // after its next await (see router.js getNavigationId()).
  const navId = getNavigationId();
  const superseded = () => getNavigationId() !== navId;
  if (!(await checkAuth()) || superseded()) return;
  if (!isSystemAdmin(getUser())) {
    showToast('Admin access required', 'error');
    navigate('/');
    return;
  }

  initLayout(getUser());
  updateLayoutContext({ projectId: null, projectName: null, activeView: 'admin', viewLabel: 'Admin' });

  runId++;
  resetState();
  const owner = state;
  state.tab = adminTabFromQuery(getQueryParams());
  getContentEl().innerHTML = renderShell(state.tab);
  bind();
  loadTab(state.tab);

  return () => {
    // A late teardown (router.js runs it as soon as it resolves) must leave a newer mount of
    // this page alone: that mount owns the module state by then.
    if (state !== owner) return;
    runId++;
    closeModal(state.modalToken);
  };
}
